import { RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { HelpService } from '~src/commonInfra/services/HelpService';

import { Messages } from '../../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../../commonInfra/components/FormikCheckbox';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useFormikContext } from '../../../../FormikWrapperForStubbing';
import { learnMoreStyle, settingsRadioButtonItemHeight } from '../../../../styles/CommonStyles';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';
import { DataAccelerationAutoSuspendSection } from './DataAccelerationAutoSuspendSection';
import { isTableauOnPrem } from './DataAccelerationSectionHelper';
import { DataAccelerationViewsLimitPerSiteField } from './DataAccelerationViewsLimitPerSiteField';

export const OWNER_ALLOWED_FOR_ACCELERATION_FIELD = 'settings.canOwnerAccelerate';
export const VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION_FIELD_ENABLED = 'settings.workbookAccelerationSettings.viewsLimitPerSiteForAccelerationEnabled';

export type DataAccelerationSectionProps = SiteSettingsForSiteAdminProps<'canOwnerAccelerate' | 'workbookAccelerationSettings'>;

export function DataAccelerationSection(props: DataAccelerationSectionProps) {
  const formik = useFormikContext<DataAccelerationSectionProps>();

  const buttons = React.useMemo(() => getButtonsList(formik.values), [formik.values]);

  const index = buttons.findIndex(button => button.value === formik.values.settings.workbookAccelerationSettings?.viewsLimitPerSiteForAccelerationEnabled);

  const handleChange = React.useCallback((value: boolean) => formik.setFieldValue(VIEWS_LIMIT_PER_SITE_FOR_ACCELERATION_FIELD_ENABLED, value), [formik]);
  return FeatureFlags.accelerationSiteAdminUIEnabled
    && SessionSiteSelectors.canOwnerAccelerate(getAppStore().getState()) !== undefined
    ? (
      <SettingsSection title={Messages.viewAcceleration()}
        testId={'data-acceleration-setting-section'}
        description={findTextWithLearnMoreLink(
          Messages.allowOwnerToAccelerate(), 'allow-owner-to-accelerate', 'server-acceleration')}>
        <FormikCheckbox
          name={OWNER_ALLOWED_FOR_ACCELERATION_FIELD}
          label={Messages.enableWorkbookOwnerToAccelerate()}
          testId={'data-acceleration-checkbox'}
        />
        &nbsp;
        {isTableauOnPrem() && <RadioButtonWithLabelGroupWidget buttonList={buttons}
          aria-label={Messages.limitNumberOfAcceleratedViews()}
          className={settingsRadioButtonItemHeight}
          checkedIndex={index}
          handleChange={handleChange}
          label={Messages.limitNumberOfAcceleratedViews()}
          testId={'data-acceleration-radio-button-group'}
        />}
        <br/>
        {FeatureFlags.autoSuspensionForAcceleratedViews && <DataAccelerationAutoSuspendSection {...props} /> }
      </SettingsSection>
    )
    : null;
}

export function findTextWithLearnMoreLink(text: string, testId: string, learnMoreToken: string) {
  return <>
    <span data-tb-test-id={testId}>
      {text}
      <TextLinkWidget
        testId={testId + '-learn-more'}
        className={learnMoreStyle}
        kind={TextLinkKind.Standard}
        href={HelpService.getHelpUrlByCategory(learnMoreToken)}
        target={'_blank'}
        rel={'noreferrer noopener'}
      >
        {Messages.learnMore()}
      </TextLinkWidget>
    </span>
  </>;
}

function getButtonsList(props: DataAccelerationSectionProps): RadioButtonWithLabelOption<boolean>[] {
  return [
    {
      value: false,
      label: findTextWithLearnMoreLink(
        Messages.noLimitOnViewsForAccelerationPerSite(),
        'data-acceleration-site-no-limit',
        'server-backgrounder-process'),
      testId: 'data-acceleration-no-limit-on-views-per-site-radio-button'
    },
    {
      value: true,
      label: <DataAccelerationViewsLimitPerSiteField {...props} />,
      testId: 'data-acceleration-limit-on-views-per-site-enabled-radio-button'
    }
  ];
}

export const DataAccelerationSectionSpecs = componentRegistryEntry({
  name: 'DataAccelerationSection',
  component: DataAccelerationSection
});